import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "../../hooks/useTranslation";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DateRangeIcon from "@mui/icons-material/DateRange";
import HistoryIcon from "@mui/icons-material/History";

interface WhenStepProps {
  state?: string;
  dateSelection?: string;
  onChange: (field: string, value: string) => void;
  isSubmitting: boolean;
}

const WhenStep: React.FC<WhenStepProps> = ({
  state,
  dateSelection,
  onChange,
  isSubmitting,
}) => {
  const { t } = useTranslation();

  const options = [
    {
      value: "lessthan3monthsCA",
      label: t("form.fifthQuestion.option1"),
      icon: AccessTimeIcon,
      color: {
        main: "#10b981",
        light: "#f0fdf4",
        border: "#bbf7d0",
        hover: "#dcfce7",
      },
    },
    {
      value: "lessthan6monthsCA",
      label: t("form.fifthQuestion.option2"),
      icon: EventIcon,
      color: {
        main: "#22c55e",
        light: "#f0fdf4",
        border: "#86efac",
        hover: "#dcfce7",
      },
    },
    {
      value: "lessthanyearCA",
      label: t("form.fifthQuestion.option3"),
      icon: CalendarTodayIcon,
      color: {
        main: "#f59e0b",
        light: "#fffbeb",
        border: "#fde68a",
        hover: "#fef3c7",
      },
    },
    {
      value: "within2yearsCA",
      label: t("form.fifthQuestion.option4"),
      icon: DateRangeIcon,
      color: {
        main: "#f97316",
        light: "#fff7ed",
        border: "#fed7aa",
        hover: "#ffedd5",
      },
    },
    {
      value: "moreThan2yearsCA",
      label: t("form.fifthQuestion.option5"),
      icon: HistoryIcon,
      color: {
        main: "#64748b",
        light: "#f8fafc",
        border: "#cbd5e1",
        hover: "#f1f5f9",
      },
    },
  ];

  return (
    <Box>
      <Typography
        variant="subtitle2"
        align="center"
        sx={{
          color: "#9ca3af",
          fontWeight: 500,
          mb: 2,
          fontSize: "0.875rem",
        }}
      >
        Question 5 out of 5
      </Typography>

      <Typography
        variant="h4"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 700,
          color: "#1e293b",
          fontSize: { xs: "1.2rem", md: "1.5rem" },
        }}
      >
        5. {t("form.fifthQuestion.question")}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {options.map((option) => {
          const isSelected = dateSelection === option.value;
          const Icon = option.icon;

          return (
            <Button
              key={option.value}
              disabled={isSubmitting}
              onClick={() => onChange("accidentDateSelection", option.value)}
              sx={{
                padding: "1.25rem 1.5rem",
                borderRadius: "12px",
                border: `2px solid ${
                  isSelected ? option.color.main : option.color.border
                }`,
                backgroundColor: isSelected
                  ? option.color.light
                  : "transparent",
                display: "flex",
                alignItems: "center",
                gap: 2,
                justifyContent: "flex-start",
                textTransform: "none",
                transition: "all 0.2s ease",
                opacity: isSubmitting ? 0.6 : 1,
                "&:hover": {
                  backgroundColor: option.color.hover,
                  borderColor: option.color.main,
                  transform: isSubmitting ? "none" : "translateX(4px)",
                },
                "&:disabled": {
                  cursor: isSubmitting ? "wait" : "not-allowed",
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: 28,
                  color: option.color.main,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  fontWeight: 500,
                  color: "#334155",
                  textAlign: "left",
                }}
              >
                {option.label}
              </Typography>
            </Button>
          );
        })}
      </Box>

      {isSubmitting && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: "#eff6ff",
            borderRadius: "8px",
            border: "1px solid #bfdbfe",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              border: "3px solid #3b82f6",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography
            sx={{
              color: "#3b82f6",
              fontWeight: 500,
              fontSize: "0.95rem",
            }}
          >
            Processing your submission...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default WhenStep;
